import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Instagram, Youtube, Send } from "lucide-react";
import { api } from "../../lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're on the list. Welcome to the studio.");
      setEmail("");
    } catch {
      toast.error("Subscription failed. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <footer className="relative border-t border-white/5 bg-[var(--bg)] text-[var(--ink)]" data-testid="site-footer">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-16">
        <div className="md:col-span-5">
          <div className="h-eyebrow mb-6">The Dispatch</div>
          <h3 className="h-display text-4xl md:text-5xl leading-none mb-8">
            Occasional letters<br/>from the <span className="italic font-serif-editorial text-[var(--gold)]">studio.</span>
          </h3>
          <form onSubmit={subscribe} className="flex items-end gap-4 max-w-md" data-testid="newsletter-form">
            <input
              type="email"
              required
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="line-input"
              data-testid="newsletter-email"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="newsletter-submit"
              className="text-[var(--gold)] border-b border-[var(--gold)]/40 pb-3 font-sans-display uppercase tracking-[0.3em] text-[11px] hover:border-[var(--gold)] transition-colors"
            >
              <span className="inline-flex items-center gap-2">{loading ? "Sending" : "Subscribe"} <Send size={12} /></span>
            </button>
          </form>
        </div>

        <div className="md:col-span-3">
          <div className="h-eyebrow mb-6">Studio</div>
          <ul className="space-y-3 font-serif-editorial text-lg text-[var(--ink)]/80">
            <li><Link className="hover:text-[var(--gold)]" to="/services">Services & Pricing</Link></li>
            <li><Link className="hover:text-[var(--gold)]" to="/portfolio">Portfolio</Link></li>
            <li><Link className="hover:text-[var(--gold)]" to="/studio">The Studio & Gear</Link></li>
            <li><Link className="hover:text-[var(--gold)]" to="/journal">The Journal</Link></li>
            <li><Link className="hover:text-[var(--gold)]" to="/contact">Commission a Project</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="h-eyebrow mb-6">Correspondence</div>
          <p className="font-serif-editorial text-lg leading-relaxed text-[var(--ink)]/80 max-w-sm">
            By private appointment only.<br/>
            Surrey, BC &mdash; Available across Canada.<br/>
            <a href="mailto:ian@sebastianvisualcorp.ca" className="text-[var(--gold)] hover:underline">ian@sebastianvisualcorp.ca</a>
          </p>
          <div className="mt-8 flex items-center gap-5">
            <a href="#" aria-label="Instagram" data-testid="social-instagram" className="text-[var(--ink-muted)] hover:text-[var(--gold)] transition-colors"><Instagram size={18} /></a>
            <a href="#" aria-label="YouTube" data-testid="social-youtube" className="text-[var(--ink-muted)] hover:text-[var(--gold)] transition-colors"><Youtube size={18} /></a>
            <a href="https://vimeo.com" target="_blank" rel="noopener noreferrer" aria-label="Vimeo" className="text-[var(--ink-muted)] hover:text-[var(--gold)] text-[11px] font-sans-display uppercase tracking-[0.3em]">Vimeo</a>
          </div>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[11px] font-sans-display uppercase tracking-[0.25em] text-[var(--ink-muted)]">
          <div>© {new Date().getFullYear()} Sebastian Visual Corp. All frames reserved.</div>
          <div>Cinematic Execution · Architectural Precision</div>
          <div className="flex items-center gap-6">
            <Link to="/legal" data-testid="footer-legal-link" className="hover:text-[var(--gold)]">Privacy &amp; Terms</Link>
            <Link to="/admin/login" data-testid="admin-portal-link" className="hover:text-[var(--gold)]">Studio Portal</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
